import {
    type AnyCopilotMessage,
    type CopilotAttachmentUploadResult,
    type CopilotSessionDetail,
} from "@regal-voice/shared-types";

import { apiHeaders } from "Services/AbstractApiService";
import { getApiUrl } from "Services/ProxyConfig";

export const getCopilotGenerateReplyUrl = (): string => `${getApiUrl()}/copilot/generate-reply`;

export const getCopilotSseHeaders = (): Record<string, string> => {
    return {
        ...apiHeaders(),
        accept: "text/event-stream",
        "Content-Type": "application/json",
    };
};

type CopilotApiErrorBody = {
    message?: string;
    detail?: string;
};

function extractErrorMessage(body: unknown): string {
    if (typeof body === "object" && body !== null) {
        const errorBody = body as CopilotApiErrorBody;
        if (typeof errorBody.detail === "string" && errorBody.detail.trim().length > 0) {
            return errorBody.detail;
        }
        if (typeof errorBody.message === "string" && errorBody.message.trim().length > 0) {
            return errorBody.message;
        }
    }
    return "Request failed";
}

async function parseResponseBody(response: Response): Promise<unknown> {
    if (response.status === 204) {
        return undefined;
    }

    const contentType = response.headers.get("Content-Type") ?? "";
    if (!contentType.includes("application/json")) {
        const text = await response.text();
        return text.length > 0 ? text : undefined;
    }

    return response.json();
}

/**
 * IMPORTANT (Copilot attachments): Use `window.fetch` directly here instead of the app fetch
 * ponyfill, which applies a global 401/403 interceptor that calls `kickOut()`.
 *
 * These endpoints can return non-auth 403s for expected business conditions (for example,
 * brand-scoped attachments feature disabled). Treating those as auth failures logs the user out,
 * which is a misleading and disruptive UX during upload flows.
 *
 * Keep this scoped to attachment/session bootstrap endpoints; do not broaden without revisiting
 * centralized unauthorized handling semantics.
 */
async function requestCopilotApi<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
    const response = await window.fetch(input, init);
    const body = await parseResponseBody(response);

    if (!response.ok) {
        throw new Error(extractErrorMessage(body));
    }

    return body as T;
}

export type CreateCopilotSessionResponse = {
    sessionId: string;
    session: CopilotSessionDetail;
};

export type CopilotAiAgentEntityReference = {
    type: "ai_agent";
    id: string;
    version: number;
    variantId?: string;
};

export async function createCopilotSession(): Promise<CreateCopilotSessionResponse> {
    return requestCopilotApi<CreateCopilotSessionResponse>(`${getApiUrl()}/copilot/sessions`, {
        method: "POST",
        headers: {
            ...apiHeaders(),
            "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
        credentials: "same-origin",
    });
}

export async function createCopilotEntityTrigger(
    sessionId: string,
    entity: CopilotAiAgentEntityReference
): Promise<AnyCopilotMessage> {
    return requestCopilotApi<AnyCopilotMessage>(
        `${getApiUrl()}/copilot/sessions/${encodeURIComponent(sessionId)}/entity-trigger`,
        {
            method: "POST",
            headers: {
                ...apiHeaders(),
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ entity }),
            credentials: "same-origin",
        }
    );
}

export async function createCopilotEntitySession(entity: CopilotAiAgentEntityReference): Promise<string> {
    const { sessionId } = await createCopilotSession();
    await createCopilotEntityTrigger(sessionId, entity);
    return sessionId;
}

export async function uploadCopilotAttachment(sessionId: string, file: File): Promise<CopilotAttachmentUploadResult> {
    const formData = new FormData();
    formData.append("file", file);

    return requestCopilotApi<CopilotAttachmentUploadResult>(
        `${getApiUrl()}/copilot/sessions/${encodeURIComponent(sessionId)}/attachments`,
        {
            method: "POST",
            headers: apiHeaders(),
            body: formData,
            credentials: "same-origin",
        }
    );
}
